/**
 * El submenú de un negocio. Vive en el Layout: así no se pierde al cambiar
 * de sección (calendario, clientes, un listado, la consola…).
 */
import { useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router'

import { useSoloLectura } from '../../auth'
import { useFichaNegocio } from '../../lib/ficha-negocio'
import { useEsAdminUi } from '../../ver-como'
import { AbrirAgenda } from './abrir-agenda'

export interface DestinoNegocio {
    sufijo: string
    etiqueta: string
    soloAdmin?: boolean
}

export const DESTINOS: DestinoNegocio[] = [
    { sufijo: '', etiqueta: 'Resumen' },
    { sufijo: '/calendar', etiqueta: 'Calendario' },
    { sufijo: '/clientes', etiqueta: 'Clientes' },
    { sufijo: '/auditoria', etiqueta: 'Movimientos' },
    { sufijo: '/supervisor', etiqueta: 'Supervisor' },
    { sufijo: '/espera', etiqueta: 'Lista de espera' },
    { sufijo: '/elevadas', etiqueta: 'Al equipo' },
    { sufijo: '/catalogo', etiqueta: 'Trabajadores y servicios' },
    { sufijo: '/info', etiqueta: 'Información' },
    { sufijo: '/console', etiqueta: 'Consola', soloAdmin: true },
    { sufijo: '/invitar', etiqueta: 'Invitar', soloAdmin: true },
]

/** ¿Esta pestaña es la de la URL? Los listados cuelgan de Resumen. */
export function pestanaActiva(pathname: string, base: string, sufijo: string): boolean {
    if (sufijo === '') {
        return (
            pathname === base ||
            pathname.startsWith(`${base}/counter`) ||
            pathname.startsWith(`${base}/cancelaciones`)
        )
    }
    return pathname === `${base}${sufijo}` || pathname.startsWith(`${base}${sufijo}/`)
}

export function migaAnterior(pathname: string, accountId: string): { to: string; etiqueta: string } | null {
    const base = `/business/${accountId}`
    if (pathname.startsWith(`${base}/counter`) || pathname.startsWith(`${base}/cancelaciones`)) {
        return { to: base, etiqueta: 'Resumen' }
    }
    if (/\/console\/[^/]+$/.test(pathname)) {
        return { to: `${base}/console`, etiqueta: 'Consola' }
    }
    if (/\/clientes\/[^/]+$/.test(pathname)) {
        return { to: `${base}/clientes`, etiqueta: 'Clientes' }
    }
    return null
}

export function SubmenuNegocio({ accountId }: { accountId: string }) {
    const { pathname } = useLocation()
    const esAdmin = useEsAdminUi()
    const base = `/business/${accountId}`
    const { nombre, chatwootUrl, esDemo } = useFichaNegocio(accountId)
    const soloLectura = useSoloLectura()
    const fila = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const filaEl = fila.current
        const el = filaEl?.querySelector('[aria-current="page"]')
        if (!filaEl || !(el instanceof HTMLElement)) return
        // Spec 020: el documento no se mueve con las pestañas.
        // `scrollIntoView` en Chrome desplaza la ventana y puede dejar
        // el scroll del documento muerto.
        const left = el.offsetLeft - (filaEl.clientWidth - el.offsetWidth) / 2
        filaEl.scrollTo({ left: Math.max(0, left) })
    }, [pathname])

    const identidad = (
        <>
            {esAdmin && (
                <Link
                    to="/"
                    className="mr-2 shrink-0 text-sm text-neutral-500 hover:text-neutral-300"
                    title="Toda la operación"
                >
                    ← Operación
                </Link>
            )}
            {nombre && (
                <span className="mr-2 min-w-0 truncate text-sm font-semibold text-neutral-100">{nombre}</span>
            )}
            {esDemo && (
                <span className="shrink-0 rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-medium text-amber-300">
                    Demo
                </span>
            )}
        </>
    )

    const pestanas = DESTINOS.filter((d) => !d.soloAdmin || esAdmin).map((d) => {
        const destino = `${base}${d.sufijo}`
        const activo = pestanaActiva(pathname, base, d.sufijo)
        return (
            <Link
                key={d.sufijo}
                to={destino}
                aria-current={activo ? 'page' : undefined}
                className={
                    'inline-flex min-h-11 shrink-0 items-center rounded-md px-2.5 text-sm transition ' +
                    (activo
                        ? 'bg-sky-500/20 text-sky-100'
                        : 'text-neutral-400 hover:bg-sky-500/10 hover:text-sky-200')
                }
            >
                {d.etiqueta}
            </Link>
        )
    })

    const extra = (
        <span className="ml-auto flex shrink-0 items-center">
            {esAdmin && <AbrirAgenda accountId={accountId} />}
            {chatwootUrl && !esDemo && !soloLectura && (
                <a
                    href={chatwootUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex min-h-11 shrink-0 items-center rounded-md px-2.5 text-sm text-neutral-400 transition hover:bg-neutral-900 hover:text-neutral-200"
                >
                    Chatwoot ↗
                </a>
            )}
        </span>
    )

    return (
        <nav className="border-t border-neutral-800/80 bg-neutral-950/95 px-[max(1rem,env(safe-area-inset-left))] py-2 pr-[max(1rem,env(safe-area-inset-right))] backdrop-blur md:px-6">
            <div className="mb-1 flex min-w-0 items-center gap-x-1 md:hidden">{identidad}</div>
            <div
                ref={fila}
                className="flex flex-nowrap items-center gap-x-1 overflow-x-auto overflow-y-clip overscroll-x-contain [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
                <span className="hidden items-center md:flex">{identidad}</span>
                {pestanas}
                {extra}
            </div>
        </nav>
    )
}
